import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Newspaper } from "lucide-react";
import { getPublishedBlogs, retailerLabel } from "../services/BlogService";
import type { BlogPost } from "../services/BlogService";
import useSeo from "../hooks/useSeo";
import { BLOG_INDEX_SEO } from "../seo/pages";

function BlogSEOHead() {
  useSeo(BLOG_INDEX_SEO);
  return null;
}

function PostRow({ post }: { post: BlogPost }) {
  const date = new Date(post.publishedAt);
  const dealCount = post.deals?.length || 0;
  const topDiscount = post.deals?.[0]?.discount;

  return (
    <Link to={`/blog/${post.slug}`} className="group block py-5 border-b border-gray-100 dark:border-dark-800 last:border-b-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            {post.retailer && (
              <span className="text-[11px] font-semibold text-primary-600 dark:text-primary-400">
                {retailerLabel(post.retailer)}
              </span>
            )}
            <span className="text-[11px] text-gray-400 dark:text-dark-500">
              {date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
          </div>
          <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white leading-snug group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors mb-1">
            {post.title}
          </h2>
          <p className="text-[13px] text-gray-500 dark:text-dark-400 leading-relaxed line-clamp-2">
            {post.excerpt}
          </p>
        </div>
        <div className="flex-shrink-0 text-right hidden sm:block pt-1">
          {topDiscount && topDiscount > 0 && (
            <span className="text-xs font-semibold text-red-500">up to {Math.round(topDiscount)}% off</span>
          )}
          {dealCount > 0 && (
            <p className="text-[11px] text-gray-400 dark:text-dark-500 mt-0.5">{dealCount} deals</p>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  const { data: posts, isLoading, error } = useQuery<BlogPost[]>({
    queryKey: ["blogs"],
    queryFn: () => getPublishedBlogs(50),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      <BlogSEOHead />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-16">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight mb-1">
            Blog
          </h1>
          <p className="text-sm text-gray-500 dark:text-dark-400">
            Daily deal roundups and clearance finds.
          </p>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 dark:border-dark-700 border-t-primary-600" />
          </div>
        )}

        {error && (
          <div className="text-center py-16">
            <Newspaper className="w-8 h-8 text-gray-300 dark:text-dark-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-dark-400">Could not load posts. Try again later.</p>
          </div>
        )}

        {posts && posts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-sm text-gray-500 dark:text-dark-400">No posts yet. Check back soon.</p>
          </div>
        )}

        {posts && posts.length > 0 && (
          <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-100 dark:border-dark-800 px-5">
            {posts.map((post) => (
              <PostRow key={post.id} post={post} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
